import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AppLayout from '../layout/AppLayout';
import { whatsappAccountService } from '../../services/api';
import { INK, MUTED, RULE, Section } from '../analytics/analyticsUi';
import TemplatePreview, { cleanText } from './TemplatePreview';

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------
const TEMPLATE_TYPES = [
  { value: 'STANDARD', label: 'Standard', text: 'Text, media or location header, message, footer and buttons.' },
  { value: 'CAROUSEL', label: 'Carousel', text: 'A message with 2 to 10 swipeable image or video cards.' },
  { value: 'LIMITED_TIME_OFFER', label: 'Limited-time offer', text: 'An offer with an optional countdown and offer code.' },
  { value: 'CATALOG', label: 'Catalog', text: 'Opens your product catalog connected in Meta.' },
  { value: 'MULTI_PRODUCT', label: 'Multi-product', text: 'Shows up to 30 products from your catalog.' },
  { value: 'AUTHENTICATION', label: 'Authentication', text: 'One-time passcodes with copy, one-tap or zero-tap delivery.' }
];

const LANGUAGES = [
  ['en', 'English'], ['en_US', 'English (US)'], ['en_GB', 'English (UK)'], ['hi', 'Hindi'], ['ml', 'Malayalam'],
  ['ta', 'Tamil'], ['te', 'Telugu'], ['kn', 'Kannada'], ['mr', 'Marathi'], ['bn', 'Bengali'], ['gu', 'Gujarati'],
  ['pa', 'Punjabi'], ['ur', 'Urdu'], ['ar', 'Arabic'], ['fr', 'French'], ['de', 'German'], ['es', 'Spanish'],
  ['pt_BR', 'Portuguese (Brazil)'], ['id', 'Indonesian'], ['ms', 'Malay'], ['zh_CN', 'Chinese (simplified)'],
  ['ru', 'Russian'], ['it', 'Italian'], ['tr', 'Turkish'], ['th', 'Thai'], ['vi', 'Vietnamese'], ['sw', 'Swahili'],
  ['ne', 'Nepali'], ['si', 'Sinhala']
];

const HEADER_FORMATS = {
  STANDARD: ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'],
  LIMITED_TIME_OFFER: ['NONE', 'IMAGE', 'VIDEO'],
  MULTI_PRODUCT: ['TEXT']
};

const FORMAT_LABELS = { NONE: 'None', TEXT: 'Text', IMAGE: 'Image', VIDEO: 'Video', DOCUMENT: 'Document', LOCATION: 'Location' };

const BUTTON_TYPES = {
  QUICK_REPLY: 'Quick reply',
  URL: 'Visit website',
  PHONE_NUMBER: 'Call phone number',
  VOICE_CALL: 'Call on WhatsApp',
  COPY_CODE: 'Copy offer code',
  FLOW: 'Open a WhatsApp Flow'
};

const allowedButtons = (templateType, category) => {
  if (templateType === 'STANDARD') {
    return ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'VOICE_CALL', 'FLOW', ...(category === 'MARKETING' ? ['COPY_CODE'] : [])];
  }
  if (templateType === 'LIMITED_TIME_OFFER') return ['COPY_CODE', 'URL'];
  if (templateType === 'CAROUSEL') return ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];
  return [];
};

const MAX_BUTTONS = { STANDARD: 10, LIMITED_TIME_OFFER: 2, CAROUSEL: 2 };

const ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf'
};

let idCounter = 0;
const newId = () => `k${Date.now().toString(36)}${(idCounter += 1)}`;

const emptyButton = (type) => ({ id: newId(), type, text: '', url: '', example: '', phoneNumber: '', flowId: '', flowAction: 'navigate', navigateScreen: '' });
const emptyCard = (buttonTypes = ['URL']) => ({
  id: newId(), handle: '', previewUrl: '', fileName: '', uploading: false,
  body: { text: '', examples: [] },
  buttons: buttonTypes.map((type) => emptyButton(type))
});

const emptyForm = (accountId = '') => ({
  accountId,
  templateType: 'STANDARD',
  name: '',
  language: 'en',
  category: 'MARKETING',
  header: { format: 'NONE', text: '', example: '', handle: '', previewUrl: '', fileName: '', uploading: false },
  body: { text: '', examples: [] },
  footer: { text: '' },
  buttons: [],
  offer: { text: '', hasExpiration: true },
  cardMediaFormat: 'IMAGE',
  cards: [emptyCard(), emptyCard()],
  authentication: {
    otpType: 'COPY_CODE',
    addSecurityRecommendation: true,
    codeExpirationMinutes: '10',
    messageSendTtlSeconds: '',
    supportedApps: [{ packageName: '', signatureHash: '' }],
    zeroTapTermsAccepted: false
  }
});

const variableCount = (text = '') => {
  const numbers = [...text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]));
  return numbers.length ? Math.max(...numbers) : 0;
};

const toTemplateName = (value) => value.toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '').slice(0, 512);

// Map a template from Meta back into the builder's form (for Duplicate)
const templateToForm = (template, accountId) => {
  const form = emptyForm(accountId);
  const components = template.components || [];
  const find = (type) => components.find((c) => c.type === type);
  const header = find('HEADER');
  const body = find('BODY');
  const footer = find('FOOTER');
  const buttons = find('BUTTONS')?.buttons || [];
  const carousel = find('CAROUSEL');
  const lto = find('LIMITED_TIME_OFFER');

  const mapButton = (b) => ({
    ...emptyButton(b.type),
    text: b.text || '',
    url: b.url || '',
    example: Array.isArray(b.example) ? b.example[0] || '' : b.example || '',
    phoneNumber: b.phone_number || '',
    flowId: b.flow_id ? String(b.flow_id) : '',
    flowAction: b.flow_action || 'navigate',
    navigateScreen: b.navigate_screen || ''
  });

  form.name = `${template.name}_copy`.slice(0, 512);
  form.language = template.language || 'en';
  form.category = template.category === 'UTILITY' ? 'UTILITY' : 'MARKETING';

  if (template.category === 'AUTHENTICATION') {
    const otp = buttons.find((b) => b.type === 'OTP') || {};
    form.templateType = 'AUTHENTICATION';
    form.authentication = {
      ...form.authentication,
      otpType: otp.otp_type || 'COPY_CODE',
      addSecurityRecommendation: !!body?.add_security_recommendation,
      codeExpirationMinutes: footer?.code_expiration_minutes ? String(footer.code_expiration_minutes) : '',
      supportedApps: (otp.supported_apps || []).length
        ? otp.supported_apps.map((a) => ({ packageName: a.package_name || '', signatureHash: a.signature_hash || '' }))
        : form.authentication.supportedApps
    };
    return form;
  }

  if (carousel) form.templateType = 'CAROUSEL';
  else if (lto) form.templateType = 'LIMITED_TIME_OFFER';
  else if (buttons.some((b) => b.type === 'CATALOG')) form.templateType = 'CATALOG';
  else if (buttons.some((b) => b.type === 'MPM')) form.templateType = 'MULTI_PRODUCT';

  if (header) {
    form.header = {
      ...form.header,
      format: header.format || 'TEXT',
      text: header.text || '',
      example: header.example?.header_text?.[0] || ''
    };
  }
  if (body) form.body = { text: cleanText(body.text || ''), examples: body.example?.body_text?.[0] || [] };
  if (footer?.text) form.footer = { text: footer.text };
  if (lto) form.offer = { text: lto.limited_time_offer?.text || '', hasExpiration: !!lto.limited_time_offer?.has_expiration };
  if (form.templateType === 'STANDARD' || form.templateType === 'LIMITED_TIME_OFFER') {
    form.buttons = buttons.filter((b) => BUTTON_TYPES[b.type]).map(mapButton);
  }

  if (carousel) {
    const firstHeader = (carousel.cards?.[0]?.components || []).find((c) => c.type === 'HEADER');
    form.cardMediaFormat = firstHeader?.format === 'VIDEO' ? 'VIDEO' : 'IMAGE';
    form.cards = (carousel.cards || []).map((card) => {
      const cardBody = (card.components || []).find((c) => c.type === 'BODY');
      const cardButtons = (card.components || []).find((c) => c.type === 'BUTTONS')?.buttons || [];
      return {
        ...emptyCard([]),
        body: { text: cleanText(cardBody?.text || ''), examples: cardBody?.example?.body_text?.[0] || [] },
        buttons: cardButtons.map(mapButton)
      };
    });
  }
  return form;
};

// ---------------------------------------------------------------------------
// Small editors
// ---------------------------------------------------------------------------
const Field = (props) => <TextField size="small" fullWidth {...props} />;

const CharCount = ({ value, max }) => (
  <Box component="span" sx={{ color: value.length > max ? '#C2410C' : MUTED }}>{value.length}/{max}</Box>
);

const VariableText = ({ label, value, onChange, examples, onExamplesChange, max, minRows = 4, placeholder, helper }) => {
  const inputRef = useRef(null);
  const count = variableCount(value);

  const addVariable = () => {
    const next = `{{${count + 1}}}`;
    const el = inputRef.current;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const updated = `${value.slice(0, start)}${next}${value.slice(end)}`;
    onChange(updated);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(start + next.length, start + next.length);
      }
    });
  };

  return (
    <Box>
      <Field
        multiline
        minRows={minRows}
        label={label}
        value={value}
        placeholder={placeholder}
        inputRef={inputRef}
        onChange={(e) => onChange(e.target.value)}
        helperText={
          <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <span>{helper || 'Use *bold*, _italic_ and ~strikethrough~.'}</span>
            <CharCount value={value} max={max} />
          </Box>
        }
      />
      <Button size="small" startIcon={<AddIcon />} onClick={addVariable} sx={{ textTransform: 'none', mt: 0.5 }}>
        Add variable
      </Button>
      {count > 0 && (
        <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {Array.from({ length: count }).map((_, i) => (
            <Field
              key={i}
              label={`Example for {{${i + 1}}}`}
              value={examples[i] || ''}
              onChange={(e) => {
                const next = [...examples];
                next[i] = e.target.value;
                onExamplesChange(next);
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const MediaUpload = ({ format, fileName, uploading, previewUrl, onFile, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
    <Button
      component="label"
      variant="outlined"
      disabled={disabled || uploading}
      startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadOutlinedIcon />}
      sx={{ textTransform: 'none' }}
    >
      {uploading ? 'Uploading…' : fileName ? `Replace ${format.toLowerCase()}` : `Upload example ${format.toLowerCase()}`}
      <input
        hidden
        type="file"
        accept={ACCEPT[format]}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </Button>
    <Typography variant="body2" sx={{ color: fileName ? INK : MUTED, minWidth: 0 }} noWrap>
      {fileName || (format === 'IMAGE' ? 'JPEG or PNG, up to 5 MB' : format === 'VIDEO' ? 'MP4, up to 16 MB' : 'PDF, up to 100 MB')}
      {previewUrl && !uploading ? ', uploaded' : ''}
    </Typography>
  </Box>
);

const ButtonEditor = ({ button, index, onChange, onRemove, typeLocked }) => {
  const set = (field) => (e) => onChange({ ...button, [field]: e.target.value });
  const hasUrlVariable = /\{\{\s*1\s*\}\}\s*$/.test(button.url);

  return (
    <Box sx={{ border: `1px solid ${RULE}`, borderRadius: 1.5, p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
        <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>
          {BUTTON_TYPES[button.type]}
          {typeLocked ? '' : ` (button ${index + 1})`}
        </Typography>
        {onRemove && (
          <IconButton size="small" onClick={onRemove} aria-label={`Remove ${BUTTON_TYPES[button.type]} button`}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {button.type !== 'COPY_CODE' && (
          <Field label="Button text" value={button.text} onChange={set('text')} inputProps={{ maxLength: 25 }} helperText={`${button.text.length}/25`} />
        )}
        {button.type === 'URL' && (
          <>
            <Field
              label="Website address"
              value={button.url}
              onChange={set('url')}
              placeholder="https://example.com/orders/{{1}}"
              helperText="Add {{1}} at the end for a different link per message"
            />
            {hasUrlVariable && (
              <Field label="Example value for {{1}}" value={button.example} onChange={set('example')} placeholder="ORDER123" />
            )}
          </>
        )}
        {button.type === 'PHONE_NUMBER' && (
          <Field label="Phone number" value={button.phoneNumber} onChange={set('phoneNumber')} placeholder="+919876543210" />
        )}
        {button.type === 'COPY_CODE' && (
          <Field label="Example offer code" value={button.example} onChange={set('example')} inputProps={{ maxLength: 15 }} helperText="Up to 15 characters" />
        )}
        {button.type === 'FLOW' && (
          <>
            <Field label="Flow ID" value={button.flowId} onChange={set('flowId')} helperText="From WhatsApp Manager, Flows" />
            <FormControl size="small" fullWidth>
              <InputLabel id={`flow-action-${button.id}`}>When opened</InputLabel>
              <Select labelId={`flow-action-${button.id}`} label="When opened" value={button.flowAction} onChange={set('flowAction')}>
                <MenuItem value="navigate">Open a screen</MenuItem>
                <MenuItem value="data_exchange">Request data from your server</MenuItem>
              </Select>
            </FormControl>
            {button.flowAction === 'navigate' && (
              <Field label="First screen ID" value={button.navigateScreen} onChange={set('navigateScreen')} placeholder="WELCOME_SCREEN" />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const AddButtonMenu = ({ types, onAdd, disabled }) => {
  const [value, setValue] = useState('');
  return (
    <FormControl size="small" sx={{ minWidth: 220 }} disabled={disabled}>
      <InputLabel id="add-button-label">Add button</InputLabel>
      <Select
        labelId="add-button-label"
        label="Add button"
        value={value}
        onChange={(e) => {
          onAdd(e.target.value);
          setValue('');
        }}
      >
        {types.map((type) => <MenuItem key={type} value={type}>{BUTTON_TYPES[type]}</MenuItem>)}
      </Select>
    </FormControl>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const TemplateBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));

  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(() => {
    const state = location.state || {};
    return state.template ? templateToForm(state.template, state.accountId || '') : emptyForm(state.accountId || '');
  });
  const [validation, setValidation] = useState({ errors: [], payload: null, checking: false });
  const [showExamples, setShowExamples] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [touched, setTouched] = useState(false);
  const duplicatedMedia = useRef(
    !!location.state?.template && (location.state.template.components || []).some(
      (c) => (c.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(c.format)) || c.type === 'CAROUSEL'
    )
  );

  useEffect(() => {
    const load = async () => {
      try {
        const response = await whatsappAccountService.getAccounts(1, 1000);
        const list = (response.data || []).filter((a) => a.wabaId);
        setAccounts(list);
        setForm((prev) => (prev.accountId || !list[0] ? prev : { ...prev, accountId: list[0]._id }));
      } catch (err) {
        setSubmitError('Your WhatsApp accounts could not be loaded.');
      }
    };
    load();
  }, []);

  // Release local preview URLs
  const objectUrls = useRef([]);
  useEffect(() => () => objectUrls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateIn = (key, patch) => setForm((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // What the backend receives (without local-only fields)
  const requestBody = useMemo(() => {
    const { accountId, header, cards, ...rest } = form;
    return {
      ...rest,
      header: { format: header.format, text: header.text, example: header.example, handle: header.handle },
      cards: cards.map((card) => ({ handle: card.handle, body: card.body, buttons: card.buttons }))
    };
  }, [form]);

  // Live validation with the same rules the server applies
  useEffect(() => {
    if (!form.accountId) return undefined;
    setValidation((prev) => ({ ...prev, checking: true }));
    const timer = setTimeout(async () => {
      try {
        const response = await whatsappAccountService.createMetaTemplate(form.accountId, requestBody, { validateOnly: true });
        setValidation({ errors: response.data.errors || [], payload: response.data.payload, checking: false });
      } catch (err) {
        setValidation((prev) => ({ ...prev, checking: false }));
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [requestBody, form.accountId]);

  // Preview: server payload plus local media previews
  const previewTemplate = useMemo(() => {
    const payload = validation.payload;
    if (!payload) return null;
    const components = (payload.components || []).map((component) => {
      if (component.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)) {
        return { ...component, previewUrl: form.header.previewUrl, fileName: form.header.fileName };
      }
      if (component.type === 'CAROUSEL') {
        return {
          ...component,
          cards: (component.cards || []).map((card, i) => ({
            components: (card.components || []).map((c) => (c.type === 'HEADER'
              ? { ...c, previewUrl: form.cards[i]?.previewUrl }
              : c))
          }))
        };
      }
      return component;
    });
    return { ...payload, components };
  }, [validation.payload, form.header.previewUrl, form.header.fileName, form.cards]);

  const previewExamples = showExamples ? {
    body: form.body.examples,
    header: [form.header.example],
    cards: form.cards.map((card) => card.body.examples)
  } : undefined;

  const uploadFile = async (file, onStart, onDone) => {
    if (!form.accountId) {
      setSubmitError('Choose a WhatsApp account before uploading.');
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    objectUrls.current.push(previewUrl);
    onStart({ uploading: true, fileName: file.name, previewUrl: '' });
    try {
      const response = await whatsappAccountService.uploadTemplateMedia(form.accountId, file);
      onDone({ uploading: false, handle: response.data.handle, previewUrl, fileName: file.name });
    } catch (err) {
      onDone({ uploading: false, handle: '', previewUrl: '', fileName: '' });
      setSubmitError(err.response?.data?.error || 'The file could not be uploaded to Meta. Try again.');
    }
  };

  const submit = async () => {
    setTouched(true);
    setSubmitError(null);
    if (validation.errors.length || validation.checking) return;
    try {
      setSubmitting(true);
      const response = await whatsappAccountService.createMetaTemplate(form.accountId, requestBody);
      navigate('/templates', {
        state: {
          accountId: form.accountId,
          notice: `${response.data.name} submitted to Meta for review (${String(response.data.status || 'pending').toLowerCase()})`
        }
      });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Meta did not accept the template. Check the details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Button helpers (standard and limited-time offer)
  const buttonTypes = allowedButtons(form.templateType, form.category);
  const maxButtons = MAX_BUTTONS[form.templateType] || 0;
  const setButton = (index, button) => update({ buttons: form.buttons.map((b, i) => (i === index ? button : b)) });

  // ---- Carousel helpers: every card shares the same button types
  const cardButtonTypes = form.cards[0]?.buttons.map((b) => b.type) || [];
  const addCardButton = (type) => update({ cards: form.cards.map((card) => ({ ...card, buttons: [...card.buttons, emptyButton(type)] })) });
  const removeCardButton = (index) => update({ cards: form.cards.map((card) => ({ ...card, buttons: card.buttons.filter((_, i) => i !== index) })) });
  const setCard = (index, patch) => setForm((prev) => ({ ...prev, cards: prev.cards.map((c, i) => (i === index ? { ...c, ...patch } : c)) }));

  const selectedType = TEMPLATE_TYPES.find((t) => t.value === form.templateType);
  const headerFormats = HEADER_FORMATS[form.templateType];
  const showCategory = form.templateType === 'STANDARD' || form.templateType === 'CATALOG';
  const showBody = form.templateType !== 'AUTHENTICATION';
  const showFooter = ['STANDARD', 'CATALOG', 'MULTI_PRODUCT'].includes(form.templateType);
  const canSubmit = !!form.accountId && !validation.errors.length && !validation.checking && !submitting;

  // ---- Checks panel
  const checks = (
    <Box>
      <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, color: INK, mb: 1 }}>Checks</Typography>
      {validation.checking && !validation.payload ? (
        <Typography variant="body2" sx={{ color: MUTED }}>Checking…</Typography>
      ) : validation.errors.length ? (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {validation.errors.slice(0, touched ? 50 : 6).map((error) => (
            <Box component="li" key={error} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
              <ErrorOutlineIcon sx={{ fontSize: 18, color: touched ? '#C2410C' : '#C77700', mt: '1px' }} />
              <Typography variant="body2" sx={{ color: INK }}>{error}</Typography>
            </Box>
          ))}
          {!touched && validation.errors.length > 6 && (
            <Typography variant="body2" sx={{ color: MUTED, pl: 3.5 }}>and {validation.errors.length - 6} more</Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#1BAA52' }} />
          <Typography variant="body2" sx={{ color: INK }}>Ready to submit. Meta usually reviews templates within minutes, sometimes up to 24 hours.</Typography>
        </Box>
      )}
    </Box>
  );

  const previewPanel = (
    <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, p: 2.5, position: isWide ? 'sticky' : 'static', top: 88 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, color: INK }}>Preview</Typography>
        <FormControlLabel
          control={<Switch size="small" checked={showExamples} onChange={(e) => setShowExamples(e.target.checked)} />}
          label={<Typography variant="body2" sx={{ color: MUTED }}>Example values</Typography>}
          sx={{ mr: 0 }}
        />
      </Box>
      {previewTemplate ? (
        <TemplatePreview template={previewTemplate} examples={previewExamples} />
      ) : (
        <Box sx={{ bgcolor: '#EFEAE2', borderRadius: 2, p: 3 }}>
          <Typography variant="body2" sx={{ color: MUTED }}>The preview appears as you fill in the template.</Typography>
        </Box>
      )}
      <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${RULE}` }}>{checks}</Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={submit}
          disabled={!canSubmit && touched}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {submitting ? 'Submitting…' : 'Submit for review'}
        </Button>
      </Box>
    </Paper>
  );

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        {/* Header */}
        <Link
          component="button"
          onClick={() => navigate('/templates')}
          underline="hover"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: MUTED, fontSize: '0.875rem', mb: 1.5 }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Message templates
        </Link>
        <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
          {location.state?.template ? 'Duplicate template' : 'Create template'}
        </Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, mb: 3, maxWidth: '70ch' }}>
          The template is sent to Meta for review. Once approved, you can use it to start conversations with customers.
        </Typography>

        {submitError && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>{submitError}</Alert>}
        {duplicatedMedia.current && (
          <Alert severity="info" variant="outlined" sx={{ mb: 3, borderColor: RULE, color: INK }}>
            Media files aren't copied from the original template. Upload the example files again.
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(360px, 5fr)' }, gap: 3, alignItems: 'start' }}>
          <Box>
            {/* Basics */}
            <Section title="Basics">
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Autocomplete
                  size="small"
                  options={accounts}
                  value={accounts.find((a) => a._id === form.accountId) || null}
                  onChange={(e, account) => account && update({ accountId: account._id })}
                  disableClearable
                  isOptionEqualToValue={(o, v) => o._id === v._id}
                  getOptionLabel={(a) => `${a.verifiedName || a.businessName}, ${a.phoneNumber}`}
                  renderInput={(params) => <TextField {...params} label="WhatsApp account" />}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
                  <Field
                    label="Template name"
                    value={form.name}
                    onChange={(e) => update({ name: toTemplateName(e.target.value) })}
                    placeholder="order_confirmation"
                    helperText="Lowercase letters, numbers and underscores. Can't be changed later."
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel id="lang-label">Language</InputLabel>
                    <Select labelId="lang-label" label="Language" value={form.language} onChange={(e) => update({ language: e.target.value })}>
                      {LANGUAGES.map(([code, label]) => <MenuItem key={code} value={code}>{label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Section>

            {/* Type */}
            <Section title="Template type">
              <Box role="radiogroup" aria-label="Template type" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.25 }}>
                {TEMPLATE_TYPES.map((type) => {
                  const selected = form.templateType === type.value;
                  return (
                    <Box
                      key={type.value}
                      component="button"
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        const headerFormat = (HEADER_FORMATS[type.value] || ['NONE']).includes(form.header.format)
                          ? form.header.format
                          : (HEADER_FORMATS[type.value] || ['NONE'])[0];
                        update({
                          templateType: type.value,
                          header: { ...form.header, format: headerFormat },
                          buttons: form.buttons.filter((b) => allowedButtons(type.value, form.category).includes(b.type))
                            .slice(0, MAX_BUTTONS[type.value] || 0)
                        });
                      }}
                      sx={{
                        all: 'unset',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1.5px solid ${selected ? theme.palette.primary.main : RULE}`,
                        bgcolor: selected ? 'rgba(37, 211, 102, 0.08)' : 'transparent',
                        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 }
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, color: INK, fontSize: '0.95rem' }}>{type.label}</Typography>
                      <Typography variant="body2" sx={{ color: MUTED, mt: 0.25 }}>{type.text}</Typography>
                    </Box>
                  );
                })}
              </Box>

              {showCategory && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="body2" sx={{ color: MUTED, mb: 0.75 }}>Category</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={form.category}
                    onChange={(e, value) => value && update({
                      category: value,
                      buttons: form.buttons.filter((b) => allowedButtons(form.templateType, value).includes(b.type))
                    })}
                  >
                    <ToggleButton value="MARKETING" sx={{ textTransform: 'none', px: 2 }}>Marketing</ToggleButton>
                    <ToggleButton value="UTILITY" sx={{ textTransform: 'none', px: 2 }}>Utility</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.75 }}>
                    Utility is for updates about something the customer did, like an order or appointment. Promotions are marketing. Meta may change the category during review.
                  </Typography>
                </Box>
              )}
              {!showCategory && selectedType && form.templateType !== 'AUTHENTICATION' && (
                <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5 }}>
                  {selectedType.label} templates are always in the marketing category.
                </Typography>
              )}
              {form.templateType === 'CATALOG' || form.templateType === 'MULTI_PRODUCT' ? (
                <Alert severity="info" variant="outlined" sx={{ mt: 2, borderColor: RULE, color: INK }}>
                  This type needs a product catalog connected to the WhatsApp Business account in Meta Commerce Manager.
                </Alert>
              ) : null}
            </Section>

            {/* Authentication */}
            {form.templateType === 'AUTHENTICATION' && (
              <Section title="Code delivery" description="WhatsApp writes the message text for authentication templates. You choose how the code reaches your app.">
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={form.authentication.otpType}
                  onChange={(e, value) => value && updateIn('authentication', { otpType: value })}
                  sx={{ flexWrap: 'wrap', mb: 2 }}
                >
                  <ToggleButton value="COPY_CODE" sx={{ textTransform: 'none', px: 2 }}>Copy code</ToggleButton>
                  <ToggleButton value="ONE_TAP" sx={{ textTransform: 'none', px: 2 }}>One-tap autofill</ToggleButton>
                  <ToggleButton value="ZERO_TAP" sx={{ textTransform: 'none', px: 2 }}>Zero-tap</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <FormControlLabel
                    control={<Checkbox checked={form.authentication.addSecurityRecommendation} onChange={(e) => updateIn('authentication', { addSecurityRecommendation: e.target.checked })} />}
                    label={<Typography variant="body2" sx={{ color: INK }}>Add "For your security, do not share this code."</Typography>}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Field
                      type="number"
                      label="Code expires after (minutes)"
                      value={form.authentication.codeExpirationMinutes}
                      onChange={(e) => updateIn('authentication', { codeExpirationMinutes: e.target.value })}
                      helperText="1 to 90. Leave empty to hide."
                    />
                    <Field
                      type="number"
                      label="Stop delivery after (seconds)"
                      value={form.authentication.messageSendTtlSeconds}
                      onChange={(e) => updateIn('authentication', { messageSendTtlSeconds: e.target.value })}
                      helperText="Optional, 30 to 900"
                    />
                  </Box>
                  {form.authentication.otpType !== 'COPY_CODE' && (
                    <Box>
                      <Typography variant="body2" sx={{ color: INK, fontWeight: 600, mb: 1 }}>Android apps that receive the code</Typography>
                      {form.authentication.supportedApps.map((app, index) => (
                        <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, gap: 1.5, mb: 1.5, alignItems: 'start' }}>
                          <Field
                            label="Package name"
                            value={app.packageName}
                            placeholder="com.example.app"
                            onChange={(e) => updateIn('authentication', {
                              supportedApps: form.authentication.supportedApps.map((a, i) => (i === index ? { ...a, packageName: e.target.value } : a))
                            })}
                          />
                          <Field
                            label="App signature hash"
                            value={app.signatureHash}
                            placeholder="11 characters"
                            onChange={(e) => updateIn('authentication', {
                              supportedApps: form.authentication.supportedApps.map((a, i) => (i === index ? { ...a, signatureHash: e.target.value } : a))
                            })}
                          />
                          <IconButton
                            aria-label="Remove app"
                            disabled={form.authentication.supportedApps.length === 1}
                            onClick={() => updateIn('authentication', { supportedApps: form.authentication.supportedApps.filter((_, i) => i !== index) })}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        disabled={form.authentication.supportedApps.length >= 5}
                        onClick={() => updateIn('authentication', { supportedApps: [...form.authentication.supportedApps, { packageName: '', signatureHash: '' }] })}
                        sx={{ textTransform: 'none' }}
                      >
                        Add another app
                      </Button>
                      {form.authentication.otpType === 'ZERO_TAP' && (
                        <FormControlLabel
                          sx={{ display: 'flex', mt: 1.5, alignItems: 'flex-start' }}
                          control={<Checkbox checked={form.authentication.zeroTapTermsAccepted} onChange={(e) => updateIn('authentication', { zeroTapTermsAccepted: e.target.checked })} />}
                          label={<Typography variant="body2" sx={{ color: INK, pt: 1 }}>I accept Meta's terms for zero-tap authentication templates on behalf of this business.</Typography>}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Section>
            )}

            {/* Header */}
            {headerFormats && (
              <Section title="Header" description={form.templateType === 'MULTI_PRODUCT' ? 'Required for multi-product templates.' : 'Optional. Shown at the top of the message.'}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={form.header.format}
                  onChange={(e, value) => value && updateIn('header', { format: value, handle: '', previewUrl: '', fileName: '' })}
                  sx={{ flexWrap: 'wrap', mb: form.header.format === 'NONE' ? 0 : 2 }}
                >
                  {headerFormats.map((format) => (
                    <ToggleButton key={format} value={format} sx={{ textTransform: 'none', px: 1.75 }}>{FORMAT_LABELS[format]}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {form.header.format === 'TEXT' && (
                  <Box sx={{ display: 'grid', gap: 1.5 }}>
                    <Field
                      label="Header text"
                      value={form.header.text}
                      onChange={(e) => updateIn('header', { text: e.target.value })}
                      helperText={<Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}><span>One variable allowed, e.g. Order {'{{1}}'}</span><CharCount value={form.header.text} max={60} /></Box>}
                    />
                    {variableCount(form.header.text) > 0 && (
                      <Field label="Example for {{1}}" value={form.header.example} onChange={(e) => updateIn('header', { example: e.target.value })} />
                    )}
                  </Box>
                )}
                {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.header.format) && (
                  <MediaUpload
                    format={form.header.format}
                    fileName={form.header.fileName}
                    uploading={form.header.uploading}
                    previewUrl={form.header.previewUrl}
                    disabled={!form.accountId}
                    onFile={(file) => uploadFile(
                      file,
                      (patch) => updateIn('header', patch),
                      (patch) => updateIn('header', patch)
                    )}
                  />
                )}
                {form.header.format === 'LOCATION' && (
                  <Typography variant="body2" sx={{ color: MUTED }}>The location is chosen each time the template is sent.</Typography>
                )}
              </Section>
            )}

            {/* Limited-time offer */}
            {form.templateType === 'LIMITED_TIME_OFFER' && (
              <Section title="Offer">
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  <Field
                    label="Offer text"
                    value={form.offer.text}
                    onChange={(e) => updateIn('offer', { text: e.target.value })}
                    placeholder="Expiring offer!"
                    helperText={<Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Shown next to the offer icon</span><CharCount value={form.offer.text} max={16} /></Box>}
                  />
                  <FormControlLabel
                    control={<Switch checked={form.offer.hasExpiration} onChange={(e) => updateIn('offer', { hasExpiration: e.target.checked })} />}
                    label={<Typography variant="body2" sx={{ color: INK }}>Show a countdown to when the offer ends</Typography>}
                  />
                </Box>
              </Section>
            )}

            {/* Body */}
            {showBody && (
              <Section title={form.templateType === 'CAROUSEL' ? 'Message above the cards' : 'Message'}>
                <VariableText
                  label="Message text"
                  value={form.body.text}
                  onChange={(text) => updateIn('body', { text })}
                  examples={form.body.examples}
                  onExamplesChange={(examples) => updateIn('body', { examples })}
                  max={form.templateType === 'LIMITED_TIME_OFFER' ? 600 : 1024}
                  placeholder="Hi {{1}}, your appointment is confirmed for {{2}}."
                />
              </Section>
            )}

            {/* Carousel cards */}
            {form.templateType === 'CAROUSEL' && (
              <Section title="Cards" description="2 to 10 cards. Every card has the same media type and the same buttons.">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={form.cardMediaFormat}
                    onChange={(e, value) => value && update({
                      cardMediaFormat: value,
                      cards: form.cards.map((card) => ({ ...card, handle: '', previewUrl: '', fileName: '' }))
                    })}
                  >
                    <ToggleButton value="IMAGE" sx={{ textTransform: 'none', px: 2 }}>Image cards</ToggleButton>
                    <ToggleButton value="VIDEO" sx={{ textTransform: 'none', px: 2 }}>Video cards</ToggleButton>
                  </ToggleButtonGroup>
                  {cardButtonTypes.length < 2 && (
                    <AddButtonMenu types={buttonTypes} onAdd={addCardButton} />
                  )}
                </Box>

                <Box sx={{ display: 'grid', gap: 2 }}>
                  {form.cards.map((card, index) => (
                    <Box key={card.id} sx={{ border: `1px solid ${RULE}`, borderRadius: 1.5, p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ fontWeight: 600, color: INK }}>Card {index + 1}</Typography>
                        <Tooltip title={form.cards.length <= 2 ? 'A carousel needs at least 2 cards' : 'Remove card'}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Remove card ${index + 1}`}
                              disabled={form.cards.length <= 2}
                              onClick={() => update({ cards: form.cards.filter((_, i) => i !== index) })}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'grid', gap: 1.5 }}>
                        <MediaUpload
                          format={form.cardMediaFormat}
                          fileName={card.fileName}
                          uploading={card.uploading}
                          previewUrl={card.previewUrl}
                          disabled={!form.accountId}
                          onFile={(file) => uploadFile(file, (patch) => setCard(index, patch), (patch) => setCard(index, patch))}
                        />
                        <VariableText
                          label="Card text (optional)"
                          value={card.body.text}
                          onChange={(text) => setCard(index, { body: { ...card.body, text } })}
                          examples={card.body.examples}
                          onExamplesChange={(examples) => setCard(index, { body: { ...card.body, examples } })}
                          max={160}
                          minRows={2}
                          helper="If one card has text, every card needs text."
                        />
                        {card.buttons.map((button, bIndex) => (
                          <ButtonEditor
                            key={button.id}
                            button={button}
                            index={bIndex}
                            onChange={(next) => setCard(index, { buttons: card.buttons.map((b, i) => (i === bIndex ? next : b)) })}
                            onRemove={index === 0 ? () => removeCardButton(bIndex) : undefined}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Button
                  startIcon={<AddIcon />}
                  disabled={form.cards.length >= 10}
                  onClick={() => update({ cards: [...form.cards, emptyCard(cardButtonTypes)] })}
                  sx={{ textTransform: 'none', mt: 2 }}
                >
                  Add card
                </Button>
              </Section>
            )}

            {/* Footer */}
            {showFooter && (
              <Section title="Footer" description="Optional. Short grey text below the message.">
                <Field
                  label="Footer text"
                  value={form.footer.text}
                  onChange={(e) => updateIn('footer', { text: e.target.value })}
                  placeholder="Reply STOP to unsubscribe"
                  helperText={<Box component="span" sx={{ display: 'flex', justifyContent: 'flex-end' }}><CharCount value={form.footer.text} max={60} /></Box>}
                />
              </Section>
            )}

            {/* Buttons */}
            {(form.templateType === 'STANDARD' || form.templateType === 'LIMITED_TIME_OFFER') && (
              <Section
                title="Buttons"
                description={form.templateType === 'STANDARD'
                  ? 'Optional. Up to 10 buttons; quick replies are shown before other buttons.'
                  : 'Add a copy offer code button, a website button, or both.'}
                action={
                  <AddButtonMenu
                    types={buttonTypes}
                    disabled={form.buttons.length >= maxButtons}
                    onAdd={(type) => update({ buttons: [...form.buttons, emptyButton(type)] })}
                  />
                }
              >
                {form.buttons.length ? (
                  <Box sx={{ display: 'grid', gap: 1.5 }}>
                    {form.buttons.map((button, index) => (
                      <ButtonEditor
                        key={button.id}
                        button={button}
                        index={index}
                        onChange={(next) => setButton(index, next)}
                        onRemove={() => update({ buttons: form.buttons.filter((_, i) => i !== index) })}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: MUTED }}>No buttons yet.</Typography>
                )}
              </Section>
            )}

            {(form.templateType === 'CATALOG' || form.templateType === 'MULTI_PRODUCT') && (
              <Section title="Button">
                <Typography variant="body2" sx={{ color: INK }}>
                  {form.templateType === 'CATALOG' ? 'A "View catalog" button is added automatically.' : 'A "View items" button is added automatically. You choose the products when sending.'}
                </Typography>
              </Section>
            )}

            {!isWide && previewPanel}
          </Box>

          {isWide && previewPanel}
        </Box>
      </Container>
    </AppLayout>
  );
};

export default TemplateBuilder;
